import React, { useState, useMemo, useCallback, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { NavLink, Link as RouterLink } from 'react-router-dom'

import clsx from 'clsx'
import PropTypes from 'prop-types'

import find from 'lodash/find'
import filterFP from 'lodash/fp/filter'
import flow from 'lodash/fp/flow'
import groupByFP from 'lodash/fp/groupBy'
import mapFP from 'lodash/fp/map'
import orderByFP from 'lodash/fp/orderBy'
import map from 'lodash/map'

import AppBar from '@material-ui/core/AppBar'
import Drawer from '@material-ui/core/Drawer'
import Hidden from '@material-ui/core/Hidden'
import IconButton from '@material-ui/core/IconButton'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import ListItemText from '@material-ui/core/ListItemText'
import { useTheme } from '@material-ui/core/styles'
import Toolbar from '@material-ui/core/Toolbar'
import Typography from '@material-ui/core/Typography'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import MenuIcon from '@material-ui/icons/Menu'

import { useT } from '@galo-monitor/i18n'
import useStickyState from '@galo-monitor/services/hooks/useStickyState'
import { selectUser } from '@galo-monitor/stores/authentication/selectorAuthentication'
import { AVAILABLE_CONTEXTS, ContextActions } from '@galo-monitor/stores/context'
import { selectCurrentContext } from '@galo-monitor/stores/context/selectorContext'
import { FarmActions } from '@galo-monitor/stores/farm'
import logo from '@galo-monitor/styles/assets/images/galomonitor-logo.svg'
import logoCompany from '@galo-monitor/styles/assets/images/logo-company.svg'
import logoFarm from '@galo-monitor/styles/assets/images/logo-farm.svg'
import logoRoot from '@galo-monitor/styles/assets/images/logo-root.svg'
import colors from '@galo-monitor/styles/colors'
import Copyright from '@galo-monitor/web-components/Copyright'
import JobsIconButton from '@galo-monitor/web-components/IconButton/JobsIconButton'
import NotificationsIconButton from '@galo-monitor/web-components/IconButton/NotificationsIconButton'
import UserIconButton from '@galo-monitor/web-components/IconButton/UserIconButton'
import Popover from '@galo-monitor/web-components/Popover'
import Tooltip from '@galo-monitor/web-components/Tooltip'
import { version } from '@galo-monitor/web-containers/package.json'
import { useRoutes } from '@galo-monitor/web-containers/routes/authenticated'

import SelectProperty from './SelectProperty'
import useStyles from './styles'
import { Footer, LinkStyledText } from './theme'
import UserMenu from './UserMenu'

function AuthenticatedLayout(props) {
  const { container, children } = props

  const dispatch = useDispatch()
  const currentContext = useSelector(selectCurrentContext)
  const { isCompany, functionalities } = useSelector(selectUser)

  const { routes, menuGroups } = useRoutes()
  const t = useT()

  const userMenuRef = useRef(null)

  const classes = useStyles()
  const theme = useTheme()
  const mobileSize = useMediaQuery(theme.breakpoints.down('xs'))


  const [smallDrawer, setSmallDrawer] = useStickyState(false, 'small-drawer')
  const [mobileOpen, setMobileOpen] = useState(false)

  const isCompanyUserAndHasAccessToFarms = useMemo(
    () => isCompany && find(functionalities, func =>
      func.slug === 'configuraco-da-fazenda-farm'
    ),
    [functionalities, isCompany]
  )

  const handleDrawerToggle = useCallback(() => {
    if (mobileSize) {
      setMobileOpen(!mobileOpen)
      setSmallDrawer(false)
    }
  }, [mobileOpen, mobileSize, setSmallDrawer])

  const logoByContext = useMemo(
    () => {
      switch (currentContext) {
        case AVAILABLE_CONTEXTS.root:
          return logoRoot
        case AVAILABLE_CONTEXTS.company:
          return logoCompany
        case AVAILABLE_CONTEXTS.farm:
        case AVAILABLE_CONTEXTS.property:
        default:
          return logoFarm
      }
    },
    [currentContext]
  )

  const toolbarActionsByContext = useMemo(
    () => {
      switch (currentContext) {
        case AVAILABLE_CONTEXTS.company:
        case AVAILABLE_CONTEXTS.farm:
        case AVAILABLE_CONTEXTS.property:
          return (
            <>
              <JobsIconButton size="small" iconSize={ 20 } iconColor={ colors.lightGrey4 } />
              <NotificationsIconButton size="small" iconSize={ 20 } iconColor={ colors.lightGrey4 } />
            </>
          )
        case AVAILABLE_CONTEXTS.root:
        default: return null
      }
    },
    [currentContext]
  )

  const drawerPaperClassByContext = useMemo(
    () => clsx(classes.drawerPaper, classes.widthTransition, {
      [classes.drawerOpen]: !smallDrawer,
      [classes.drawerClose]: smallDrawer,
      [classes.drawerRoot]: currentContext === AVAILABLE_CONTEXTS.root,
      [classes.drawerCompany]: currentContext === AVAILABLE_CONTEXTS.company,
      [classes.drawerFarm]: currentContext === AVAILABLE_CONTEXTS.farm || currentContext === AVAILABLE_CONTEXTS.property
    }),
    [classes.drawerClose, classes.drawerCompany, classes.drawerFarm, classes.drawerOpen, classes.drawerPaper, classes.drawerRoot, classes.widthTransition, currentContext, smallDrawer]
  )

  const appBarClassByDrawerState = useMemo(
    () => clsx(classes.widthTransition, {
      [classes.appBarDrawerOpened]: smallDrawer,
      [classes.appBar]: !smallDrawer
    }) ,
    [classes.appBar, classes.appBarDrawerOpened, classes.widthTransition, smallDrawer]
  )

  const drawerToolbarClassByContext = useMemo(
    () => clsx(classes.displayFlex, {
      [classes.brandToolbarContainerRoot]: currentContext === AVAILABLE_CONTEXTS.root,
      [classes.brandToolbarContainerCompany]: currentContext === AVAILABLE_CONTEXTS.company,
      [classes.brandToolbarContainerFarm]: currentContext === AVAILABLE_CONTEXTS.farm || currentContext === AVAILABLE_CONTEXTS.property
    }),
    [classes.brandToolbarContainerCompany, classes.brandToolbarContainerFarm, classes.brandToolbarContainerRoot, classes.displayFlex, currentContext]
  )

  const menuItems = useMemo(
    () => {
      const menusByGroups = flow(
        filterFP((item) => item.menu),
        groupByFP(item => item.menu.group)
      )(routes)

      return flow(
        orderByFP('order', 'asc'),
        mapFP(group => ({
          ...group,
          items: menusByGroups[group.group]
        })),
        filterFP(group => group.items),
        mapFP(group => (
          <div key={ group.group } className={ classes.menuSectionContainer }>
            {group.title && !smallDrawer && (
              <div className={ classes.menuSectionTitle }>
                {group.title}
              </div>
            )}
            {map(group.items, (item) => (
              <NavLink
                key={ item.path }
                to={ item.path }
                exact={ item.menu?.exact !== undefined ? item.menu.exact : item.exact }
                className={ classes.menuLink }
                activeClassName={ classes.menuActive }
              >
                <ListItem button onClick={ handleDrawerToggle }>
                  <ListItemIcon classes={ { root: classes.listItemIcon } }>
                    <Tooltip title={ item.title } placement="left">
                      <item.menu.Icon />
                    </Tooltip>
                  </ListItemIcon>
                  {!smallDrawer && <ListItemText classes={ { root: classes.listItemText } } primary={ item.title } />}
                </ListItem>
              </NavLink>
            ))}
          </div>
        ))
      )(menuGroups)
    },
    [routes, menuGroups, classes.menuSectionContainer, classes.menuSectionTitle, classes.menuLink, classes.menuActive, classes.listItemIcon, classes.listItemText, smallDrawer, handleDrawerToggle]
  )

  const drawer = (
    <div>
      <div className={ drawerToolbarClassByContext }>
        <RouterLink to="/">
          <div className={ classes.toolbar }>
            <div className={ clsx(drawerToolbarClassByContext, classes.brandToolbarContainer) }>
              <img alt="Galo Monitor" src={ logo } />
              {!smallDrawer && <img alt="Galo Monitor Logo" src={ logoByContext } style={ { marginLeft: 10 } } />}
            </div>
          </div>
        </RouterLink>
        {!smallDrawer && !mobileSize && <IconButton onClick={ () => setSmallDrawer(!smallDrawer) } style={ { color: colors.white } }>
          <ChevronLeftIcon fontSize="small" />
        </IconButton>}
      </div>

      <div className={ classes.menuItemsContainer }>
        <List>{menuItems}</List>
      </div>
    </div>
  )

  return (
    <div className={ classes.root }>

      <AppBar color="default" position="fixed" className={ appBarClassByDrawerState }>
        <Toolbar classes={ { root: classes.toolbarRoot } }>
          <div className={ classes.toolbarRightContainer }>
            {
              isCompanyUserAndHasAccessToFarms &&
              [AVAILABLE_CONTEXTS.farm, AVAILABLE_CONTEXTS.property].includes(currentContext)
                ? (
                  <LinkStyledText
                    onClick={ () => {
                      dispatch(ContextActions.initContextCompany())
                      dispatch(FarmActions.leaveFarmContext())
                    } }
                    style={ {
                      alignSelf: 'center', marginRight: 20
                    } }
                  >
                    {t('back to company')}
                  </LinkStyledText>
                ) : null
            }

            {[AVAILABLE_CONTEXTS.farm, AVAILABLE_CONTEXTS.property].includes(currentContext) && (
              <SelectProperty />
            )}

            { toolbarActionsByContext }

            <Popover
              ref={ userMenuRef }
              popoverId="user-menu"
              Component={ UserIconButton }
              ComponentProps={ {
                edge: 'end',
                fab: true,
                iconColor: colors.white,
                size: 'small',
                classes: { root: classes.userMenuButton }
              } }
            >
              <UserMenu popoverRef={ userMenuRef } />
            </Popover>
          </div>

          <IconButton
            color="inherit"
            edge="start"
            onClick={ handleDrawerToggle }
            className={ classes.menuButton }
          >
            <MenuIcon />
          </IconButton>

          {smallDrawer && !mobileSize && <IconButton onClick={ () => setSmallDrawer(!smallDrawer) }>
            <ChevronRightIcon fontSize="small" />
          </IconButton>}
        </Toolbar>
      </AppBar>

      <nav className={ classes.drawer } aria-label="mailbox folders">
        {/* The implementation can be swapped with js to avoid SEO duplication of links. */}
        <Hidden smUp implementation="css">
          <Drawer
            container={ container }
            variant="temporary"
            anchor={ theme.direction === 'rtl' ? 'right' : 'left' }
            open={ mobileOpen }
            onClose={ handleDrawerToggle }
            classes={ {
              paper: drawerPaperClassByContext
            } }
            ModalProps={ {
              keepMounted: true // Better open performance on mobile.
            } }
          >
            {drawer}
          </Drawer>
        </Hidden>
        <Hidden xsDown implementation="css">
          <Drawer
            classes={ {
              paper: drawerPaperClassByContext
            } }
            variant="permanent"
            open
          >
            {drawer}
          </Drawer>
        </Hidden>
      </nav>

      <main className={ clsx(classes.container, { [classes.containerDrawerOpened]: smallDrawer }) }>
        <div className={ classes.toolbar } />

        <div className={ classes.content }>
          {children}
        </div>

        <Footer className={ classes.footer }>
          <Typography variant="caption">{`v ${ version }`}</Typography>
          <Copyright />
          <Typography variant="caption">&nbsp;|&nbsp;{t('copyright message')}</Typography>
        </Footer>
      </main>

    </div>
  )
}

AuthenticatedLayout.propTypes = {
  /**
   * Injected by the documentation to work in an iframe.
   * You won't need it on your project.
   */
  container: PropTypes.element,
  children: PropTypes.element
}

AuthenticatedLayout.defaultProps = {
  children: null,
  container: null
}

export default AuthenticatedLayout
